import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { catalogProducts } from '@/mocks/catalog';
import { useCart } from '@/hooks/useCart';

// Рекомендуем товары из категорий: нейропротекторы → витамины и БАД, обезболивающие
const SIMILAR_IDS = [17, 21, 10, 16, 23, 2, 15, 24];

const similarProducts = catalogProducts.filter(p => SIMILAR_IDS.includes(p.id));

const REASONS = [
  'Похожий состав',
  'Та же категория',
  'Часто берут вместе',
  'Популярный выбор',
  'Схожее действие',
  'Рекомендуют врачи',
  'Хит продаж',
  'Выгодная цена',
];

const SimilarProducts = () => {
  const { addItem, hasItem } = useCart();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'discount' | 'rating' | 'price'>('all');

  const filtered = [...similarProducts].sort((a, b) => {
    if (activeFilter === 'discount') return b.discount - a.discount;
    if (activeFilter === 'rating') return b.rating - a.rating;
    if (activeFilter === 'price') return a.price - b.price;
    return b.reviews - a.reviews;
  });

  const scroll = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
  };

  return (
    <section className="py-16 px-4 md:px-8 lg:px-12" style={{ background: '#050508' }}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-[#f0ff00]/50 mb-2">ПЕРСОНАЛЬНЫЕ РЕКОМЕНДАЦИИ</p>
            <h2 className="font-orbitron font-black text-2xl md:text-3xl text-white">
              ПОХОЖИЕ <span style={{ color: '#f0ff00', textShadow: '0 0 20px #f0ff0060' }}>ТОВАРЫ</span>
            </h2>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-1.5 p-1 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            {([
              { id: 'all', label: 'Популярные' },
              { id: 'discount', label: 'Скидки' },
              { id: 'rating', label: 'Рейтинг' },
              { id: 'price', label: 'Дешевле' },
            ] as { id: typeof activeFilter; label: string }[]).map(f => (
              <button
                key={f.id}
                onClick={() => setActiveFilter(f.id)}
                className="whitespace-nowrap px-3 py-1.5 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200"
                style={activeFilter === f.id
                  ? { background: '#f0ff00', color: '#050508', boxShadow: '0 0 10px #f0ff0060' }
                  : { color: 'rgba(255,255,255,0.35)' }
                }
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Scroll controls */}
        <div className="relative">
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 hidden md:flex"
            style={{ background: 'rgba(240,255,0,0.1)', border: '1px solid rgba(240,255,0,0.25)', color: '#f0ff00' }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'rgba(240,255,0,0.2)'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'rgba(240,255,0,0.1)'}
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>

          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 hidden md:flex"
            style={{ background: 'rgba(240,255,0,0.1)', border: '1px solid rgba(240,255,0,0.25)', color: '#f0ff00' }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'rgba(240,255,0,0.2)'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'rgba(240,255,0,0.1)'}
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>

          {/* Scrollable cards */}
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {filtered.map((product, index) => {
              const color = product.color;
              const reason = REASONS[index % REASONS.length];
              const inCart = hasItem(product.id);

              return (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="flex-shrink-0 w-[220px] rounded-2xl overflow-hidden group cursor-pointer transition-all duration-300 flex flex-col"
                  style={{ border: `1px solid ${color}15`, background: 'rgba(255,255,255,0.02)' }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = `${color}40`;
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = `${color}15`;
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                  }}
                >
                  {/* Image */}
                  <div
                    className="relative h-44 flex items-center justify-center overflow-hidden"
                    style={{ background: `linear-gradient(135deg, #0a0a14, ${color}08)` }}
                  >
                    {/* Discount badge */}
                    {product.discount > 0 && (
                      <div className="absolute top-2.5 left-2.5 z-10">
                        <span
                          className="px-2 py-0.5 rounded-full font-orbitron font-black text-[10px]"
                          style={{ background: color, color: '#050508', boxShadow: `0 0 8px ${color}80` }}
                        >
                          -{product.discount}%
                        </span>
                      </div>
                    )}

                    {/* Reason tag */}
                    <div className="absolute top-2.5 right-2.5 z-10">
                      <span
                        className="px-2 py-0.5 rounded-full font-mono text-[9px]"
                        style={{ background: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.1)' }}
                      >
                        {reason}
                      </span>
                    </div>

                    <div
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 h-10 rounded-full opacity-15 group-hover:opacity-35 transition-opacity"
                      style={{ background: color, filter: 'blur(14px)' }}
                    />
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-28 h-28 object-contain relative z-10 transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-1"
                      style={{ filter: `drop-shadow(0 6px 14px ${color}50)` }}
                    />

                    {/* Glow line on hover */}
                    <div
                      className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
                    />
                  </div>

                  {/* Info */}
                  <div className="p-4 flex flex-col flex-1">
                    {/* Category */}
                    <span
                      className="font-mono text-[9px] px-2 py-0.5 rounded-full mb-2 self-start"
                      style={{ background: `${color}12`, color, border: `1px solid ${color}25` }}
                    >
                      {product.category}
                    </span>

                    <h3 className="font-orbitron font-bold text-xs text-white leading-snug mb-1 line-clamp-2 flex-1">
                      {product.name}
                    </h3>

                    {/* Rating */}
                    <div className="flex items-center gap-1 mb-3">
                      {[1,2,3,4,5].map(s => (
                        <i key={s} className={`text-[9px] ${s <= Math.round(product.rating) ? 'ri-star-fill' : 'ri-star-line'}`} style={{ color: '#f0ff00' }} />
                      ))}
                      <span className="font-mono text-[10px] text-white/30 ml-0.5">{product.rating}</span>
                    </div>

                    {/* Price */}
                    <div className="flex items-end justify-between mb-3">
                      <div>
                        <div
                          className="font-orbitron font-black text-lg leading-none"
                          style={{ color, textShadow: `0 0 8px ${color}60` }}
                        >
                          {product.price.toLocaleString()}₽
                        </div>
                        {product.oldPrice && (
                          <div className="font-mono text-[10px] text-white/25 line-through mt-0.5">
                            {product.oldPrice.toLocaleString()}₽
                          </div>
                        )}
                      </div>
                      {product.discount > 0 && (
                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(57,255,20,0.1)', color: '#39ff14', border: '1px solid rgba(57,255,20,0.2)' }}>
                          -{(product.oldPrice! - product.price).toLocaleString()}₽
                        </span>
                      )}
                    </div>

                    {/* CTA */}
                    <button
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        addItem({ id: product.id, name: product.name, price: product.price, oldPrice: product.oldPrice ?? undefined, image: product.image, color });
                      }}
                      className="whitespace-nowrap w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-mono text-xs cursor-pointer transition-all duration-300"
                      style={inCart
                        ? { background: color, color: '#050508', boxShadow: `0 0 12px ${color}80` }
                        : { border: `1px solid ${color}`, color }
                      }
                    >
                      <i className={inCart ? 'ri-check-line' : 'ri-shopping-bag-line'} />
                      {inCart ? 'В КОРЗИНЕ' : 'В КОРЗИНУ'}
                    </button>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Bottom link */}
        <div className="mt-8 text-center">
          <Link
            to="/catalog"
            className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3 rounded-full font-mono text-sm cursor-pointer transition-all duration-300"
            style={{ border: '1px solid rgba(240,255,0,0.3)', color: '#f0ff00' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 0 16px rgba(240,255,0,0.2)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
          >
            <i className="ri-grid-line" />
            Смотреть весь каталог
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SimilarProducts;
